import { useApi } from "@api/ApiProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  addCustomPromptApi,
  deleteCustomPromptApi,
  documentToPromptApi,
  editCustomPromptApi,
} from "./customPrompts.api";
import { customPromptsQueryKey } from "./utils";

type AddCustomPromptParams = Parameters<typeof addCustomPromptApi>[1];
type EditCustomPromptParams = Parameters<typeof editCustomPromptApi>[1];
type DocumentToPromptParams = Parameters<typeof documentToPromptApi>[1];

export const useCustomPromptsMutations = () => {
  const { api } = useApi();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: customPromptsQueryKey() });

  const uploadPromptMutation = useMutation({
    mutationFn: (params: AddCustomPromptParams) => addCustomPromptApi(api, params),
    onSuccess: invalidate,
  });

  const deletePromptMutation = useMutation({
    mutationFn: (promptID: string) => deleteCustomPromptApi(api, promptID),
    onSuccess: invalidate,
  });

  const editPromptMutation = useMutation({
    mutationFn: (params: EditCustomPromptParams) => editCustomPromptApi(api, params),
    onSuccess: invalidate,
  });

  const editOverrideMutation = useMutation({
    mutationFn: (params: EditCustomPromptParams) => editCustomPromptApi(api, { ...params, override: true }),
    onSuccess: invalidate,
  });

  // Conversion endpoint — returns prompt suggestions extracted from a file. Does
  // not write to the customPrompts catalog, so no invalidation.
  const documentToPromptMutation = useMutation({
    mutationFn: (params: DocumentToPromptParams) => documentToPromptApi(api, params),
  });

  return {
    uploadPromptMutation,
    deletePromptMutation,
    editPromptMutation,
    editOverrideMutation,
    documentToPromptMutation,
  };
};
